package com.evoapps.evoshift.ui.user

import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.MyLocation
import androidx.compose.material3.Text
import androidx.compose.foundation.clickable
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.ui.text.style.TextAlign
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.asString
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import com.evoapps.evoshift.domain.model.ClockStamp
import com.evoapps.evoshift.domain.model.LocationCheck
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.EvoDialog
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.at_work_location_outside
import evoshift.composeapp.generated.resources.at_work_location_unverified
import evoshift.composeapp.generated.resources.cancel
import evoshift.composeapp.generated.resources.location_notice_continue
import evoshift.composeapp.generated.resources.location_notice_text
import evoshift.composeapp.generated.resources.location_notice_title
import org.jetbrains.compose.resources.stringResource
import androidx.compose.material.icons.automirrored.filled.Logout
import androidx.compose.material3.SnackbarDuration
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.SnackbarResult
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.getValue
import com.evoapps.evoshift.viewmodels.ClockOutUndo
import evoshift.composeapp.generated.resources.clock_out
import evoshift.composeapp.generated.resources.clock_out_done
import evoshift.composeapp.generated.resources.clock_out_early_text
import evoshift.composeapp.generated.resources.clock_out_early_title
import evoshift.composeapp.generated.resources.clock_out_keep_working
import evoshift.composeapp.generated.resources.copy_undo

const val LOCATION_NOTICE_TAG = "location_notice"

/** Amber, for clock times the manager should look at (outside the area, no location). */
val LocationFlagColor = Color(0xFFE65100)

/**
 * SH-106: why the location is read, shown once per device before the first reading (GDPR art. 13
 * information: purpose, legal basis, what is kept). Closing it clocks nothing.
 */
@Composable
fun LocationNoticeDialog(onAnswer: (accepted: Boolean) -> Unit) {
    EvoDialog(
        title = stringResource(Res.string.location_notice_title),
        icon = Icons.Default.MyLocation,
        onDismiss = { onAnswer(false) },
        modifier = Modifier.testTag(LOCATION_NOTICE_TAG),
        confirm = DialogAction(stringResource(Res.string.location_notice_continue), { onAnswer(true) }),
        dismiss = DialogAction(stringResource(Res.string.cancel), { onAnswer(false) })
    ) {
        Text(stringResource(Res.string.location_notice_text), color = Color.White.copy(alpha = 0.9f))
    }
}

/** The manager's line for a flagged clock time, or null when the stamp has nothing to flag. */
@Composable
fun locationFlagText(stamp: ClockStamp?): String? = when (stamp?.locationCheck) {
    LocationCheck.OUTSIDE -> stringResource(Res.string.at_work_location_outside, stamp.distanceMeters ?: 0)
    LocationCheck.UNVERIFIED -> stringResource(Res.string.at_work_location_unverified)
    else -> null
}

const val CLOCK_NOTE_TAG = "clock_note"

/** SH-106: "done, but your manager will see it marked" under the clock button (tap to hide). */
@Composable
fun ClockNote(note: UiText?, onDismiss: () -> Unit) {
    // SH-158: it unfolds under the button and folds away, keeping its words while it goes.
    val shown = com.evoapps.evoshift.ui.theme.rememberLastNonNull(note)
    com.evoapps.evoshift.ui.theme.MotionVisibility(note != null) {
        Text(
            shown?.asString().orEmpty(),
            color = LocationFlagColor,
            style = MaterialTheme.typography.bodyMedium,
            textAlign = TextAlign.Center,
            modifier = Modifier.testTag(CLOCK_NOTE_TAG).clickable(onClick = onDismiss)
        )
    }
}

const val EARLY_CLOCK_OUT_TAG = "early_clock_out"

/**
 * SH-165: "Planned end 22:00. Clock out now?" before a clock-out well before the planned end (a
 * second tap or a pocket tap would otherwise end the shift at once). Closing it clocks nothing.
 */
@Composable
fun EarlyClockOutDialog(plannedEnd: String, onAnswer: (confirmed: Boolean) -> Unit) {
    EvoDialog(
        title = stringResource(Res.string.clock_out_early_title),
        icon = Icons.AutoMirrored.Filled.Logout,
        onDismiss = { onAnswer(false) },
        modifier = Modifier.testTag(EARLY_CLOCK_OUT_TAG),
        confirm = DialogAction(stringResource(Res.string.clock_out), { onAnswer(true) }),
        dismiss = DialogAction(stringResource(Res.string.clock_out_keep_working), { onAnswer(false) })
    ) {
        Text(stringResource(Res.string.clock_out_early_text, plannedEnd), color = Color.White.copy(alpha = 0.9f))
    }
}

/**
 * SH-165: "Clocked out" with Undo for about 10 seconds (SnackbarDuration.Long) after each
 * clock-out; Undo opens the shift again.
 */
@Composable
fun ClockOutUndoSnackbar(undo: ClockOutUndo?, host: SnackbarHostState, onUndo: () -> Unit, onDismiss: () -> Unit) {
    val text = stringResource(Res.string.clock_out_done)
    val action = stringResource(Res.string.copy_undo)
    val latestUndo by rememberUpdatedState(onUndo)
    val latestDismiss by rememberUpdatedState(onDismiss)
    LaunchedEffect(undo) {
        if (undo == null) return@LaunchedEffect
        val result = host.showSnackbar(text, actionLabel = action, withDismissAction = true, duration = SnackbarDuration.Long)
        if (result == SnackbarResult.ActionPerformed) latestUndo() else latestDismiss()
    }
}
